import type { CustomRuleInput } from '~/types/loyaltyContract';

import { CUSTOM_RULE_IDENTIFIER_FIELDS, type CustomRuleIdentifierType } from './customRuleFieldValidation';

/** Identifier types that support batch create (text paste or product-type multi-select). */
export type BatchCustomRuleIdentifierType = CustomRuleIdentifierType;

export interface BatchCreateFailureItem {
  value: string;
  message: string;
}

export interface BatchCreateFailureSummary {
  failedValues: Array<BatchCreateFailureItem>;
  hasPerValueDetails: boolean;
  message?: string;
}

const UNKNOWN_BATCH_FAILURE_MESSAGE = 'Not created; exact invalid row not returned';

const IDENTIFIER_FIELDS: ReadonlyArray<keyof Pick<CustomRuleInput, CustomRuleIdentifierType>> =
  CUSTOM_RULE_IDENTIFIER_FIELDS;

export function parseBatchIdentifierValues(value: string): Array<string> {
  const seen = new Set<string>();

  return value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter((item) => {
      if (!item || seen.has(item)) {
        return false;
      }

      seen.add(item);
      return true;
    });
}

export function buildBatchCustomRules(
  baseRule: CustomRuleInput,
  identifierType: BatchCustomRuleIdentifierType,
  identifierValues: Array<string>,
): Array<CustomRuleInput> {
  return identifierValues.map((identifierValue) => {
    const rule = {
      ...baseRule,
      ...Object.fromEntries(IDENTIFIER_FIELDS.map((field) => [field, null])),
      [identifierType]: identifierValue,
    };

    return rule;
  });
}

interface ApiErrorObjectItem {
  path: string;
  message: string;
}

function isApiErrorObjectItem(item: unknown): item is ApiErrorObjectItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof (item as ApiErrorObjectItem).path === 'string' &&
    typeof (item as ApiErrorObjectItem).message === 'string'
  );
}

function getRuleIndexFromPath(path: string): number | null {
  const match = path.match(/^(?:rules(?:\.|\[))?(\d+)\]?\.?/);
  if (!match) {
    return null;
  }

  return Number(match[1]);
}

export function getBatchCreateFailureSummary(
  apiErrors: unknown,
  submittedValues: Array<string>,
  fallbackMessage?: string,
): BatchCreateFailureSummary {
  const failedValues: Array<BatchCreateFailureItem> = [];
  const fallbackMessages: Array<string> = [];

  if (Array.isArray(apiErrors)) {
    apiErrors.forEach((item) => {
      if (isApiErrorObjectItem(item)) {
        const index = getRuleIndexFromPath(item.path);
        if (index != null && submittedValues[index]) {
          failedValues.push({ value: submittedValues[index], message: item.message });
          return;
        }
        fallbackMessages.push(item.message);
        return;
      }

      if (typeof item === 'string') {
        const match = item.match(/^([\w.[\]]+):\s*(.+)$/);
        if (match) {
          const [, path, message] = match;
          const index = getRuleIndexFromPath(path);
          if (index != null && submittedValues[index]) {
            const fieldPath = path.replace(/^(?:rules(?:\.|\[))?\d+\]?\.?/, '');
            failedValues.push({
              value: submittedValues[index],
              message: fieldPath ? `${fieldPath}: ${message}` : message,
            });
            return;
          }
        }
        fallbackMessages.push(item);
      }
    });
  }

  if (failedValues.length > 0) {
    return { failedValues, hasPerValueDetails: true };
  }

  return {
    failedValues: submittedValues.map((value) => ({ value, message: UNKNOWN_BATCH_FAILURE_MESSAGE })),
    hasPerValueDetails: false,
    message: fallbackMessages[0] ?? fallbackMessage,
  };
}
